"use client"
import Icon from '@components/icon'
import Loader from '@components/loader'
import { faPause, faPlay } from '@fortawesome/free-solid-svg-icons'
import { usePlayer } from '@hooks'
import { useAppDispatch } from '@redux/hook'
import { pause, play } from '@redux/slices/playerSlice'
import styles from './play.module.scss'

// `Play` reads the *global* player status and nothing else, so a page that
// renders one button per station would light every one of them up at once.
// This variant compares the playing url against its own before deciding, which
// is what any list, grid or hub of stations needs.
interface IPlayToggle {
    url: string
    /** Station name — the button is icon-only, so it carries the label. */
    name: string
    /** Station id, kept in player state for attribution (app prompt links). */
    id?: string
    size?: 'sm' | 'md' | 'lg'
    /** Overrides the url comparison. Two stations can share one stream url — the
     *  mizrahit pair does — and a caller that knows which of them the listener
     *  actually picked should say so, or both light up at once. */
    active?: boolean
    /** Extra class, for callers that position the button themselves. */
    className?: string
    onPlay?: () => void
}

const LOADER: Record<'sm' | 'md' | 'lg', number> = { sm: 14, md: 20, lg: 30 }

export default function PlayToggle({ url, name, id, size = 'md', active: activeProp, className, onPlay }: IPlayToggle) {
    const dispatch = useAppDispatch()
    const player = usePlayer()

    const active = activeProp ?? (Boolean(url) && player.url === url)
    const loading = active && player.status === 'loading'
    const playing = active && player.status === 'playing'

    const toggle = () => {
        // Stopping is always allowed. `Play` used to bail out while the status
        // was 'loading', which meant a feed that never came up — a dead stream,
        // a blocked autoplay — left the button stuck with no way to silence it.
        // `loading` implies `active`, so the branch below covers that case.
        if (active) {
            dispatch(pause())
            return
        }
        dispatch(play({ url, name, id }))
        onPlay?.()
    }

    return (
        <button
            type="button"
            onClick={toggle}
            aria-pressed={active}
            aria-label={playing? `עצור את ${name}`: `נגן את ${name} בשידור חי`}
            title={playing? `עצור את ${name}`: `האזינו ל${name} בשידור חי`}
            className={`${styles.container} ${styles.button} ${styles[size]} click animated ${active? styles.on: ''} ${className || ''}`}
        >
            {
                loading? <Loader size={LOADER[size]} color='#fff' />:
                playing? <Icon icon={faPause} className={`animated-slow ${styles.active}`} />:
                <Icon icon={faPlay} />
            }
        </button>
    )
}
